import { AxiosError } from 'axios';
import { CUBE_VOC_CUSTOMER_FEEDBACK, CUBE_VOC_TO_INTERACTION_LINK } from '@/api/hcp/interaction/types';
import { VocCacheKeys } from '@/api/hcp/voiceofthecustomer/cacheKeys';
import useQuery from '@/api/queries/useQuery';
import usePartnerScoreGlobalFilter from '@/components/partner/usePartnerScoreGlobalFilter';
import { useInteractionChannelFilters } from '@/context/ChanelFocus/AdditionalFiltersContext';
import { Channel } from '@/models/channelFocus';
import { sortRawVoCMetricsStatsByPeriods } from '@/pages/ChannelFocus/components/VoiceOfTheCustomer/VoiceOfCustomerMetrics/utils';
import { CubeQuery } from '@/types/cube';
import { useGetAdditionalFilter, useGetDateRangeTimeDimensionComparison } from '@/utils/cube/hooks';
import { getNonAnonymousHCPsFilters } from '@/utils/VoiceOfCustomers/vocUtils';

const CSAT_MEASURE = 'CSAT';

type SatisfactionByPeriod = {
  current: number | undefined;
  previous: number | undefined;
};

export interface EmailSatisfaction extends SatisfactionByPeriod {
  isLoading: boolean;
}

// CSAT is null when a period has no scored responses, which must stay distinct from a measured 0.
const toCsatRatio = (periodStats: Record<string, number | undefined>) => {
  const value = periodStats[CSAT_MEASURE];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

export default function useGetEmailSatisfaction(): EmailSatisfaction {
  const dateRange = useGetDateRangeTimeDimensionComparison(`${CUBE_VOC_TO_INTERACTION_LINK}.interaction_date`);
  const { globalFiltersWithoutHcpCountryCode, countries } = usePartnerScoreGlobalFilter();
  const { getFiltersForChannel } = useInteractionChannelFilters();
  // Email tab filters apply whichever tab is active, so the KPI matches the Email page VoC widget and the coverage score.
  const emailFilters = useGetAdditionalFilter(getFiltersForChannel(Channel.EMAIL), Channel.EMAIL);

  const query: CubeQuery = {
    measures: [`${CUBE_VOC_CUSTOMER_FEEDBACK}.${CSAT_MEASURE}`],
    filters: [
      {
        member: `${CUBE_VOC_TO_INTERACTION_LINK}.voc_to_interaction_link_country_code`,
        operator: 'equals',
        values: countries,
      },
      {
        member: `${CUBE_VOC_CUSTOMER_FEEDBACK}.customer_feedback_type`,
        operator: 'equals',
        values: [Channel.EMAIL],
      },
      globalFiltersWithoutHcpCountryCode,
      emailFilters,
      ...getNonAnonymousHCPsFilters(globalFiltersWithoutHcpCountryCode),
    ],
    timeDimensions: [dateRange],
    limit: 1,
  };

  const { data, isLoading, error } = useQuery<Record<string, string>[], AxiosError, SatisfactionByPeriod>({
    payload: query,
    options: {
      queryKey: [...VocCacheKeys.getEmailSatisfaction(query)],
      select: (rows): SatisfactionByPeriod => {
        const { currentPeriod, periodToCompare } = sortRawVoCMetricsStatsByPeriods({ data: rows, dateRange });
        return {
          current: toCsatRatio(currentPeriod),
          previous: toCsatRatio(periodToCompare),
        };
      },
    },
  });

  return {
    isLoading,
    current: error ? undefined : data?.current,
    previous: error ? undefined : data?.previous,
  };
}
